/**
 * NIB Subscription Types (FINAL)
 *
 * Backend is source of truth for tier config (prices, features).
 * TIER_FALLBACK is for loading/offline states only.
 */

// ============================================================================
// ENUMS
// ============================================================================

export type SubscriptionTier = 'FREE' | 'BASIC' | 'PROFESSIONAL' | 'ENTERPRISE';
export type SubscriptionStatus =
  | 'ACTIVE'
  | 'CANCELLED'
  | 'EXPIRED'
  | 'SUSPENDED';
export type BillingPeriod = 'MONTHLY' | 'YEARLY';

// ============================================================================
// INTERFACES (Match Backend DTOs exactly)
// ============================================================================

export interface Subscription {
  id: number;
  user_id: number;
  tier: SubscriptionTier;
  status: SubscriptionStatus;
  employee_limit: number;
  billing_period: BillingPeriod;
  price: number;
  api_key: string;
  started_at: string;
  expires_at: string | null;
  features: string[];
  created_at: string;
  updated_at: string;
}

export interface UsageStats {
  current_period: CurrentPeriodStats;
  history: MonthlyHistoryStats[];
}

export interface CurrentPeriodStats {
  submissions_count: number;
  employees_processed: number;
  employee_limit: number;
  period_start: string;
  period_end: string;
}

export interface MonthlyHistoryStats {
  month: string;
  submissions: number;
  employees_processed: number;
}

/**
 * Response from check-limit endpoint
 * Matches backend LimitCheckDTO exactly
 */
export interface LimitCheckResponse {
  allowed: boolean;
  current_count: number;
  limit: number;
  remaining: number;
  tier: SubscriptionTier;
  upgrade_available: boolean;
}

/**
 * Tier info from backend GET /subscriptions/tiers
 * This is the SOURCE OF TRUTH for pricing and features
 */
export interface SubscriptionTierInfo {
  tier: SubscriptionTier;
  name: string;
  employee_limit: number;
  price: number;
  yearly_price: number;
  features: string[];
  billing_periods: BillingPeriod[];
}

export interface BillingHistoryItem {
  id: string;
  date: string;
  description: string;
  amount: number;
  status: 'PAID' | 'PENDING' | 'FAILED';
  invoice_url?: string;
}

// ============================================================================
// FALLBACK CONFIG (Loading/Offline States ONLY)
// ============================================================================

/**
 * Fallback tier config for loading states.
 * Always prefer data from useTiers() hook.
 */
export const TIER_FALLBACK: Record<
  SubscriptionTier,
  {
    tier: SubscriptionTier;
    name: string;
    employee_limit: number;
  }
> = {
  FREE: { tier: 'FREE', name: 'Free', employee_limit: 3 },
  BASIC: { tier: 'BASIC', name: 'Basic', employee_limit: 10 },
  PROFESSIONAL: {
    tier: 'PROFESSIONAL',
    name: 'Professional',
    employee_limit: 50,
  },
  ENTERPRISE: { tier: 'ENTERPRISE', name: 'Enterprise', employee_limit: -1 },
};

// UI colors (not from backend - frontend-only concern)
export const TIER_COLORS: Record<
  SubscriptionTier,
  { color: string; gradient: string }
> = {
  FREE: { color: 'gray', gradient: 'from-gray-400 to-gray-600' },
  BASIC: { color: 'blue', gradient: 'from-blue-400 to-blue-600' },
  PROFESSIONAL: { color: 'purple', gradient: 'from-purple-400 to-purple-600' },
  ENTERPRISE: { color: 'amber', gradient: 'from-amber-400 to-amber-600' },
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

export function isUnlimitedTier(limit: number): boolean {
  return limit === -1;
}

export function formatEmployeeLimit(limit: number): string {
  return limit === -1 ? 'Unlimited' : `${limit}`;
}

export function getUsagePercentage(current: number, limit: number): number {
  if (limit === -1) return 0;
  return Math.min(100, (current / limit) * 100);
}

export function getUsageColorClass(percentage: number): {
  text: string;
  bg: string;
  border: string;
} {
  if (percentage >= 90)
    return { text: 'text-red-600', bg: 'bg-red-500', border: 'border-red-200' };
  if (percentage >= 70)
    return {
      text: 'text-yellow-600',
      bg: 'bg-yellow-500',
      border: 'border-yellow-200',
    };
  return {
    text: 'text-green-600',
    bg: 'bg-green-500',
    border: 'border-green-200',
  };
}

export function getNextTier(
  currentTier: SubscriptionTier
): SubscriptionTier | null | undefined {
  const order: SubscriptionTier[] = [
    'FREE',
    'BASIC',
    'PROFESSIONAL',
    'ENTERPRISE',
  ];
  const currentIndex = order.indexOf(currentTier);
  return currentIndex < order.length - 1 ? order[currentIndex + 1] : null;
}
